import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import logo from "./assets/app-icon.png";
import "./tokenlyglobal.css";

function Header() {
  const location = useLocation();
  const isDoctorPage = ['/doctorslist', '/id-registration', '/token-book', '/login', '/forgot-password', '/tracking', '/medicine-schedule', '/register-otp', '/trackii'].includes(location.pathname) || (location.pathname.startsWith('/doctor') && location.pathname !== '/doctor-settings') || location.pathname.startsWith('/token-receipt');
  const bodyClass = isDoctorPage ? "subpage" : "";




  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [profileDetails, setProfileDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editGender, setEditGender] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editNameTitle, setEditNameTitle] = useState("");
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen]);



  // read user from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("tokenly_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        setEditName(parsed.name || "");
        setEditPhone(parsed.phone || "");
        setEditNameTitle(parsed.name_title || "");
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, [location.pathname]);

  // Navbar scroll effect
  useEffect(() => {
    const navbar = document.getElementById("main-navbar");
    if (!navbar) return;

    const handleScroll = () => {
      if (location.pathname === '/id-registration') {
        // Special handling for id-registration page
        if (window.scrollY > 50) {
          navbar.classList.add("navbar-scrolled");
          navbar.style.backgroundColor = '#111827';
          navbar.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
        } else {
          navbar.classList.remove("navbar-scrolled");
          navbar.style.backgroundColor = 'transparent';
          navbar.style.boxShadow = 'none';
        }
      } else if (isDoctorPage || window.scrollY > 50) {
        // Original logic for other pages
        navbar.classList.add("navbar-scrolled");
        navbar.style.backgroundColor = '#111827';
        navbar.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
      } else {
        navbar.classList.remove("navbar-scrolled");
        navbar.style.backgroundColor = 'transparent';
        navbar.style.boxShadow = 'none';
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDoctorPage, location.pathname]);

  // Auto-fill edit fields when profile loads
  useEffect(() => {
    if (profileDetails && showModal) {
      setEditEmail(profileDetails.email || "");
      setEditGender(profileDetails.gender || "");
      setEditCity(profileDetails.city || "");
      setEditAddress(profileDetails.street_address || "");
      setEditNameTitle(profileDetails.name_title || "");
    }
  }, [profileDetails, showModal]);

  const handleLogout = () => {
    localStorage.removeItem("tokenly_token");
    localStorage.removeItem("tokenly_user");
    setUser(null);
    setShowModal(false);
    window.location.href = "/";
  };

  const handleEditSave = async () => {
    setErrorMsg("");
    setLoading(true);

    const token = localStorage.getItem("tokenly_token");
    if (!token) {
      setErrorMsg("No token found. Please login again.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("https://tokenly.biswaas.com/api/user/profile-update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          name_title: editNameTitle,
          fullname: editName,
          phone: editPhone,
          email: editEmail,
          gender: editGender,
          city: editCity,
          street_address: editAddress,
        }),
      });

      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Update failed.");
      } else {
        // ✅ Use FULL API response data (has fullname, phone, etc.)
        const updatedUser = {
          phone: json.data.phone,
          name: json.data.fullname,
          name_title: json.data.name_title,
          doctorId: json.data.doctor_id || json.data.id, // Still null for patients
          roleId: user?.roleId, // Preserve existing roleId
        };
        localStorage.setItem("tokenly_user", JSON.stringify(updatedUser));
        setUser(updatedUser);

        // ✅ IMMEDIATE refresh with API data - NO fetchUserDetails needed
        setProfileDetails(json.data); // Shows updated fullname, phone, etc. instantly

        setEditMode(false);
      }
    } catch (err) {
      setErrorMsg("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    if (!user?.doctorId) {
      console.log("No doctorId found, using basic user info");
      return;
    }

    const token = localStorage.getItem("tokenly_token");
    if (!token) {
      setErrorMsg("No token found. Please login again.");
      return;
    }

    try {
      setDetailsLoading(true);
      setErrorMsg("");

      const res = await fetch("https://tokenly.biswaas.com/api/user-detail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(
          user?.roleId === 4
            ? { userId: user.userId }
            : { doctorId: user.doctorId }
        ),
      });

      const json = await res.json();

      if (!json.status) {
        setErrorMsg(json.message || "Failed to load profile details.");
        return;
      }

      setProfileDetails(json.data);
    } catch (err) {
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setDetailsLoading(false);
    }
  };

  const openModal = () => {
    setShowModal(true);
    setEditName(user?.name || "");
    setEditPhone(user?.phone || "");
    setEditNameTitle(user?.name_title || "");
    fetchUserDetails();
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
  };

  return (
    <React.Fragment>
      <header id="main-navbar" className={`fixed-top navbar-transition ${bodyClass}`}>
        <nav className="navbar navbar-expand-lg navbar-dark">
          <div className="container-fluid container-xl px-4 px-md-3 d-flex align-items-center">
            <Link className="navbar-brand d-flex align-items-center fw-bold fs-4 tracking-wider" to="/">
              <img
                src={logo}
                alt="Logo"
                width="35"
                height="35"
                className="rounded-circle me-2 border border-2 border-light"
              />
              Tokenly
            </Link>

            {/* Download App Button */}
            <div className="d-none d-lg-flex me-auto ms-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.doctorplus4"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-light btn-sm px-3 py-1 d-flex align-items-center gap-2"
              >
                <i className="bi bi-download"></i>
                <span className="small">Download Our App</span>
              </a>
            </div>

            <style jsx>{`
              @keyframes blink-download {
                0%, 100% { 
                  opacity: 1; 
                  box-shadow: 0 0 5px rgba(255,255,255,0.3);
                }
                50% { 
                  opacity: 0.7; 
                  box-shadow: 0 0 15px rgba(255,255,255,0.6);
                }
              }
              @keyframes shine-download {
                0% { left: -100%; }
                50% { left: 100%; }
                100% { left: -100%; }
              }
            `}</style>

            {/* Right side: Fullscreen, user icon or login icon + toggler */}
            <div className="d-flex align-items-center order-lg-last">



              {/* Profile Dropdown */}
              <div className="profile-dropdown-container position-relative">
                {user ? (
                  <>
                    {/* User Profile Icon */}
                    <div
                      className="rounded-circle bg-light text-dark d-flex align-items-center justify-content-center me-2"
                      style={{ width: 32, height: 32, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                      title={user.name || user.phone}
                      onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                    >
                      {(user.name || user.phone || "?")
                        .toString()
                        .trim()
                        .charAt(0)
                        .toUpperCase()}
                    </div>

                    {/* Dropdown Menu for Logged In User */}
                    {profileDropdownOpen && (
                      <div className="position-absolute end-0 mt-2" style={{ zIndex: 1000, minWidth: '160px' }}>
                        <div className="bg-white border rounded shadow-lg">
                          <div className="p-3 border-bottom">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                                style={{ width: 40, height: 40, fontSize: 16, fontWeight: 700 }}>
                                {(user.name || user.phone || "?")
                                  .toString()
                                  .trim()
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div>
                                <div className="fw-bold">{(profileDetails?.name_title || user?.name_title || "") + " " + (user.name || 'User')}</div>
                                <div className="small text-muted">{user.phone || 'No phone'}</div>
                              </div>
                            </div>
                          </div>
                          <div className="py-1">
                            {user?.roleId === 6 && (
                              <Link
                                to="/alldoctors"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-person-badge me-2"></i>Find Doctor
                              </Link>
                            )}
                            {user?.roleId === 6 && (
                              <Link
                                to="/token-history"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-ticket-perforated me-2"></i>Token History
                              </Link>
                            )}
                            {(user?.roleId === 6) && (
                              <Link
                                to="/wallet"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-wallet2 me-2"></i>Wallet
                              </Link>
                            )}
                            {user?.roleId === 6 && (
                              <Link
                                to="/trackii"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-activity me-2"></i>Live Tracking
                              </Link>
                            )}
                            {user?.roleId === 6 && (
                              <Link
                                to="/transaction-history"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-credit-card me-2"></i>Transaction History
                              </Link>
                            )}


                            {(user?.roleId === 3 || user?.roleId === 4) && (
                              <Link
                                to="/doctor-dashboard"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i class="bi bi-bag-dash"></i>  Dashboard
                              </Link>
                            )}



                            {(user?.roleId === 3) && (
                              <Link
                                to="/clinic-hours"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-clock me-2"></i>Clinic Hours
                              </Link>
                            )}

                            {(user?.roleId === 3 || user?.roleId === 4) && (
                              <Link
                                to="/self-mode"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-ticket-perforated me-2"></i>Self Mode
                              </Link>
                            )}
                             {(user?.roleId === 3 || user?.roleId === 4) && (
                              <Link
                                to="/view-mode"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-eye me-2"></i>View Mode
                              </Link>
                            )}


                            {(user?.roleId === 3) && (
                              <Link
                                to="/clinic-profile"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-building me-2"></i>Clinic Profile
                              </Link>
                            )}
                            {(user?.roleId === 3 || user?.roleId === 4) && (
                              <Link
                                to="/next-holiday"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-calendar-event me-2"></i>Next Holiday
                              </Link>
                            )}
                            {(user?.roleId === 3) && (
                              <Link
                                to="/staff"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-people-fill me-2"></i>My Staff
                              </Link>
                            )}
                            {(user?.roleId === 3) && (
                              <Link
                                to="/reports"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-file-earmark-text me-2"></i>Reports
                              </Link>
                            )}
                            {(user?.roleId === 3 || user?.roleId === 4) && (
                              <Link
                                to="/doctor-settings"
                                className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-decoration-none text-dark"
                                onClick={() => setProfileDropdownOpen(false)}
                              >
                                <i className="bi bi-gear me-2"></i>Settings
                              </Link>
                            )}
                            <hr className="my-1" />

                            <button
                              className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2"
                              onClick={() => {
                                setProfileDropdownOpen(false);
                                openModal();
                              }}
                            >
                              <i className="bi bi-person me-2"></i>View Profile
                            </button>
                            <button
                              className="dropdown-item w-100 text-start border-0 bg-transparent px-3 py-2 text-danger"
                              onClick={() => {
                                setProfileDropdownOpen(false);
                                handleLogout();
                              }}
                            >
                              <i className="bi bi-box-arrow-right me-2"></i>Logout
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Login Icon */}
                    <div
                      className="rounded-circle bg-light text-dark d-flex align-items-center justify-content-center me-2"
                      style={{ width: 32, height: 32, fontSize: 18, cursor: 'pointer' }}
                      onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                      title="Login/Signup"
                    >
                      👤
                    </div>

                    {/* Dropdown Menu for Logged Out User */}
                    {profileDropdownOpen && (
                      <div className="position-absolute end-0 mt-2" style={{ zIndex: 1000, minWidth: '150px' }}>
                        <div className="bg-white border rounded shadow-lg">
                          <div className="py-1">
                            <Link
                              to="/login"
                              className="dropdown-item d-block px-3 py-2 text-decoration-none text-dark hover:bg-light"
                              onClick={() => setProfileDropdownOpen(false)}
                            >
                              <i className="bi bi-box-arrow-in-right me-2"></i>Login
                            </Link>
                            <Link
                              to="/login"
                              className="dropdown-item d-block px-3 py-2 text-decoration-none text-dark hover:bg-light"
                              onClick={() => setProfileDropdownOpen(false)}
                            >
                              <i className="bi bi-person-plus me-2"></i>Sign Up
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#tokenlyNavbar"
                aria-controls="tokenlyNavbar"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon" />
              </button>
            </div>

            <div className="collapse navbar-collapse" id="tokenlyNavbar">
              <ul className="navbar-nav ms-auto me-lg-3 mb-2 mb-lg-0 align-items-center">
                <li className="nav-item">
                  <Link className={`nav-link ${location.pathname === '/' ? 'active' : ''}`} to="/">Home</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} to="/about">About Us</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`} to="/contact">Contact</Link>
                </li>
                <li className="nav-item">
                  <Link className={`nav-link ${location.pathname === '/videos' ? 'active' : ''}`} to="/videos">Videos</Link>
                </li>
                <li className="nav-item">
                  {(user?.roleId !== 3 && user?.roleId !== 4) && (
                    <Link className={`nav-link ${location.pathname === '/alldoctors' ? 'active' : ''}`} to="/alldoctors">Find Doctor</Link>
                  )}
                </li>
                {!user && (
                  <li className="nav-item me-3">
                    <Link className={`nav-link ${location.pathname === '/id-registration' ? 'active' : ''}`} to="/id-registration">Service Providers</Link>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/* User Profile Right Side Card */}
      {showModal && (
        <div className="profile-modal-overlay d-flex justify-content-end align-items-start p-3" style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1060 }} onClick={closeModal}>
          <div className="profile-card-modern bg-white shadow-lg rounded-4 overflow-hidden" style={{ width: '320px', marginTop: '80px', marginRight: '20px' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header bg-white border-0 p-3 pb-0 d-flex justify-content-between align-items-center">
              <h6 className="fw-bold mb-0">My Profile</h6>
              <button type="button" className="btn-close small" onClick={closeModal} style={{ fontSize: '0.8rem' }}></button>
            </div>

            <div className="card-body p-3">
              {errorMsg && <div className="alert alert-danger py-1 px-2 small mb-2" style={{ fontSize: '0.75rem' }}>{errorMsg}</div>}

              {editMode ? (
                <div className="edit-mode-form small">
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>TITLE</label>
                    <select className="form-select form-select-sm" value={editNameTitle || ""} onChange={(e) => setEditNameTitle(e.target.value)}>
                      <option value="">Select</option>
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Miss">Miss</option>
                      <option value="Dr">Dr</option>
                      <option value="Prof">Prof</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>NAME</label>
                    <input type="text" className="form-control form-control-sm" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>PHONE</label>
                    <input type="tel" className="form-control form-control-sm" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>EMAIL</label>
                    <input type="email" className="form-control form-control-sm" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>GENDER</label>
                    <select className="form-select form-select-sm" value={editGender || ""} onChange={(e) => setEditGender(e.target.value)}>
                      <option value="">Select</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>CITY</label>
                    <input type="text" className="form-control form-control-sm" value={editCity} onChange={(e) => setEditCity(e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.7rem' }}>ADDRESS</label>
                    <textarea className="form-control form-control-sm" rows="1" value={editAddress} onChange={(e) => setEditAddress(e.target.value)} />
                  </div>
                </div>
              ) : (
                <div className="view-mode-details">
                  {profileDetails?.profile_photo_full && (
                    <div className="text-center mb-3">
                      <img
                        src={profileDetails.profile_photo_full}
                        alt="Profile"
                        className="rounded-circle border"
                        style={{ width: 60, height: 60, objectFit: 'cover' }}
                      />
                    </div>
                  )}

                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">Title</span>
                    <span className="fw-bold small">{profileDetails?.name_title || user?.name_title || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">Name</span>
                    <span className="fw-bold small">{profileDetails?.fullname || user?.name || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">Phone</span>
                    <span className="text-dark small">{profileDetails?.phone || user?.phone || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">Email</span>
                    <span className="text-dark small">{profileDetails?.email || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">Gender</span>
                    <span className="text-dark small">{profileDetails?.gender || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                    <span className="text-muted small">City</span>
                    <span className="text-dark small">{profileDetails?.city || "N/A"}</span>
                  </div>
                  <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between align-items-start">
                    <span className="text-muted small">Address</span>
                    <div className="text-dark small text-end ps-3 custom-scrollbar" style={{ maxWidth: '200px', maxHeight: '42px', overflowY: 'auto', lineHeight: '1.2' }}>
                      {profileDetails?.street_address || "N/A"}
                    </div>
                  </div>
                  {user?.roleId !== 3 && user?.roleId !== 4 && (
                    <div className="profile-detail-row py-2 border-bottom d-flex justify-content-between">
                      <span className="text-muted small">Wallet</span>
                      <span className="text-success fw-bold small">₹{profileDetails?.balance || 0}</span>
                    </div>
                  )}
                </div>
              )}


              {detailsLoading && (
                <div className="text-center py-2">
                  <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>
              )}
            </div>

            <div className="card-footer border-0 bg-white p-3 pt-0 d-flex gap-2">
              {editMode ? (
                <>
                  <button type="button" className="btn btn-sm btn-outline-secondary w-50" onClick={() => setEditMode(false)}>Close</button>
                  <button type="button" className="btn btn-sm btn-primary w-50" onClick={handleEditSave} disabled={loading}>
                    {loading ? "..." : "Save"}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn btn-sm btn-outline-primary w-50" onClick={() => setEditMode(true)}>Edit</button>
                  <button type="button" className="btn btn-sm btn-outline-danger w-50" onClick={handleLogout}>Logout</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default Header;
